/**
 * useTemplateSelection Hook
 * 
 * Custom hook for template selection and management
 * Following SOLID principles:
 * - Single Responsibility: Handles template selection state only
 * - Open/Closed: Extensible through additional template types
 * - Interface Segregation: Only exposes necessary methods
 */

import { useState, useCallback } from 'react';
import { UseTemplateSelectionReturn, TemplateData } from '../../types/Step0.types';

/**
 * useTemplateSelection Hook
 * 
 * Features:
 * - Template selection state management
 * - Template pre-fill functionality
 * - Selection clearing utilities
 * - Type-safe template handling
 */
export const useTemplateSelection = (): UseTemplateSelectionReturn => {
  // State management
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateData | null>(null);

  /**
   * Select a template and return its description
   * This function handles template selection and returns the description for prompt pre-filling
   */
  const selectTemplate = useCallback((template: TemplateData): string => {
    // Validate template input
    if (!template) {
      console.warn('useTemplateSelection: Invalid template provided');
      return '';
    }

    // Validate template structure
    if (!template.id || !template.description) {
      console.warn('useTemplateSelection: Template missing required fields (id, description)');
      return '';
    }

    // Set selected template
    setSelectedTemplate(template);

    // Return description for prompt pre-filling
    return template.description;
  }, []);

  /**
   * Clear template selection
   * Utility function to reset template selection state
   */
  const clearSelection = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  // Return hook interface
  return {
    selectedTemplate,
    selectTemplate,
    clearSelection,
  };
};
